import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "motion/react";
import { Home, Search, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { SEO } from "@/components/SEO";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <PageTransition>
      <SEO
        title="404 - העמוד לא נמצא"
        description="העמוד שחיפשת לא נמצא. חזור לדף הבית או חפש את המוצר שאתה מחפש."
        noindex={true}
      />
      <div className="min-h-screen flex flex-col bg-background" dir="rtl">
        <Header />
        
        <main className="flex-1 flex items-center justify-center px-4 py-20">
          {/* Background decorations */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/[0.03] rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-primary/[0.05] rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10 text-center max-w-2xl mx-auto">
            {/* Animated 404 Number */}
            <motion.div
              className="relative mb-8"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              {/* Decorative ring */}
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              >
                <div className="w-48 h-48 md:w-64 md:h-64 rounded-full border border-dashed border-primary/20"></div>
              </motion.div>
              
              {/* Main 404 */}
              <div className="relative">
                <motion.span
                  className="text-[120px] md:text-[180px] font-light text-primary/10 select-none leading-none block"
                  style={{ fontFamily: 'serif' }}
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  404
                </motion.span>
                
                {/* Sparkle decorations */}
                <motion.div
                  className="absolute top-4 right-8 text-primary/40"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.6 }}
                >
                  <Sparkles className="w-6 h-6" />
                </motion.div>
                <motion.div
                  className="absolute bottom-8 left-12 text-primary/30"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.8 }}
                >
                  <Sparkles className="w-4 h-4" />
                </motion.div>
              </div>
            </motion.div>

            {/* Message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h1 className="text-2xl md:text-3xl font-light mb-4 text-foreground">
                אופס! העמוד לא נמצא
              </h1>
              <p className="text-muted-foreground text-lg mb-2">
                נראה שהתכשיט שחיפשת התגלגל למקום אחר...
              </p>
              <p className="text-muted-foreground/70 text-sm mb-8">
                העמוד שניסית לגשת אליו לא קיים או הועבר
              </p>
            </motion.div>

            {/* Action buttons */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Link to="/">
                <Button size="lg" className="gap-2 min-w-[180px]">
                  <Home className="w-4 h-4" />
                  חזרה לדף הבית
                </Button>
              </Link>
              
              <Link to="/shop">
                <Button variant="outline" size="lg" className="gap-2 min-w-[180px]">
                  <Search className="w-4 h-4" />
                  לחנות התכשיטים
                </Button>
              </Link>
            </motion.div>

            {/* Quick links */}
            <motion.div
              className="mt-12 pt-8 border-t border-border/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <p className="text-sm text-muted-foreground mb-4">או בקרו באחד מהעמודים הפופולריים:</p>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                {[
                  { name: 'טבעות', href: '/category/rings' },
                  { name: 'שרשראות', href: '/category/necklaces' },
                  { name: 'צמידים', href: '/category/bracelets' },
                  { name: 'עגילים', href: '/category/earrings' },
                  { name: 'צור קשר', href: '/contact' },
                ].map((link, index) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.8 + index * 0.1 }}
                  >
                    <Link
                      to={link.href}
                      className="text-sm text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1 group"
                    >
                      {link.name}
                      <ArrowRight className="w-3 h-3 rotate-180 group-hover:-translate-x-1 transition-transform" />
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </main>

        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default NotFound;
